import {
  BiconomySmartAccountV2,
  CreateSessionDataParams,
  PaymasterMode,
  Session,
  SessionLocalStorage,
  SessionStoragePayload,
  createBatchSession,
  createSessionKeyEOA,
  createSessionKeyManagerModule,
  createSessionSmartAccountClient,
  createSmartAccountClient,
  getSingleSessionTxParams,
} from '@biconomy/account';
import { ConnectedWallet } from '@privy-io/react-auth';
import { zeroHash } from 'viem';
import { encodeFunctionData, parseAbi } from 'viem/utils';
import config from '@/common/config';

export async function findOrCreateBiconomyAccountFromEOA({
  wallet,
  smartWalletAddress,
}: {
  wallet: ConnectedWallet;
  smartWalletAddress: `0x${string}` | undefined;
}): Promise<BiconomySmartAccountV2> {
  //   await wallet.switchChain(CHAIN_ID);
  const provider = await wallet.getEthersProvider();
  const signer = provider.getSigner();
  console.log('Creating Biconomy account...', {
    wallet,
    smartWalletAddress,
    provider,
    signer,
  });

  const usersSmartAccount = await createSmartAccountClient({
    signer: signer,
    bundlerUrl: process.env.REACT_APP_BICONOMY_BUNDLER_URL || '',
    biconomyPaymasterApiKey: process.env.REACT_APP_BICONOMY_PAYMASTER_API_KEY,
    rpcUrl: process.env.REACT_APP_RPC_URL,
    // if smartAccountAddress is provided, it will be used to find the smart account
    accountAddress: smartWalletAddress,
  });

  return usersSmartAccount;
}

export async function createSessionKey(smartAccount: BiconomySmartAccountV2): Promise<Partial<SessionStoragePayload>> {
  const { sessionKeyAddress, sessionStorageClient } = await createSessionKeyEOA(smartAccount, config.chain);

  return { sessionKeyAddress, sessionStorageClient };
}

export async function reconstructSession(smartAccountAddress: `0x${string}`, sessionIDInfo: string[]) {
  if (!sessionIDInfo || sessionIDInfo.length === 0) {
    return null;
  }
  // try to reconstruct the session storage client and session
  const reconstructedSessionStorageClient = new SessionLocalStorage(smartAccountAddress);
  const reconstructedSession: Session = {
    sessionStorageClient: reconstructedSessionStorageClient,
    sessionIDInfo,
  };
  return reconstructedSession;
}

export async function createUserBatchSession(
  smartAccount: BiconomySmartAccountV2,
  sessionStorageClient: any,
  sessionParams: CreateSessionDataParams[],
) {
  const { wait, session } = await createBatchSession(smartAccount, sessionStorageClient, sessionParams, {
    paymasterServiceData: { mode: PaymasterMode.SPONSORED },
  });

  const {
    receipt: { transactionHash },
    success,
  } = await wait();

  console.log('User session created', { session, transactionHash, success });

  return { newSession: session, transactionHash, success };
}

export async function createEmulatedUserSmartAccount(session: Session) {
  console.log('Creating emulated user smart account...', session);
  const emulatedUsersSmartAccount = await createSessionSmartAccountClient(
    {
      accountAddress: session.sessionStorageClient.smartAccountAddress, // Dapp can set the account address on behalf of the user
      bundlerUrl: process.env.REACT_APP_BICONOMY_BUNDLER_URL || '',
      biconomyPaymasterApiKey: process.env.REACT_APP_BICONOMY_PAYMASTER_API_KEY,
      chainId: config.chain?.id,
    },
    session.sessionStorageClient.smartAccountAddress,
    true,
  );
  console.log('Emulated user smart account created', emulatedUsersSmartAccount);
  return emulatedUsersSmartAccount;
}

// TODO: Add the ability to delete a session
export async function deleteSession(session: Session, sessionKeyAddress: `0x${string}`) {
  const sessionKeyManagerModuleABI = parseAbi(['function setMerkleRoot(bytes32 _merkleRoot)']);
  const setMerkleRootData = encodeFunctionData({
    abi: sessionKeyManagerModuleABI,
    functionName: 'setMerkleRoot',
    args: [zeroHash],
  });

  const singleSessionEmulatedUser = await createSessionSmartAccountClient(
    {
      accountAddress: session.sessionStorageClient.smartAccountAddress, // Dapp can set the account address on behalf of the user
      bundlerUrl: process.env.REACT_APP_BICONOMY_BUNDLER_URL || '',
      biconomyPaymasterApiKey: process.env.REACT_APP_BICONOMY_PAYMASTER_API_KEY,
      chainId: config.chain?.id,
    },
    session.sessionStorageClient.smartAccountAddress,
  );

  const sessionModule = await createSessionKeyManagerModule({
    smartAccountAddress: singleSessionEmulatedUser.biconomySmartAccountConfig.accountAddress as `0x${string}`,
    sessionStorageClient: session.sessionStorageClient,
  });

  console.log(sessionModule.getAddress());
  const deleteSessionTx = {
    to: sessionModule.getAddress(),
    data: setMerkleRootData,
  };
  const singleSessionParamsForDeletion = await getSingleSessionTxParams(session, config.chain, 4);

  const { wait } = await singleSessionEmulatedUser.sendTransaction(deleteSessionTx, {
    ...singleSessionParamsForDeletion,
    paymasterServiceData: {
      mode: PaymasterMode.SPONSORED,
      skipPatchCallData: true,
    },
  });

  const deleteSessionTxReceipt = await wait();
  console.log(deleteSessionTxReceipt);

  // await session.sessionStorageClient.setMerkleRoot('0x0');
}

export const loadSession = async (smartAccount: BiconomySmartAccountV2, localStorageKey: string) => {
  let batchSession = null;
  const batchSessionIDInfo = localStorage.getItem(localStorageKey);
  const parsedSessionIDInfo = batchSessionIDInfo ? JSON.parse(batchSessionIDInfo) : null;
  const smartAccountAddress = await smartAccount.getAddress();
  const reconstructedSession = await reconstructSession(smartAccountAddress, parsedSessionIDInfo);
  batchSession = reconstructedSession;
  return batchSession;
};
